import { LSelect } from "./ui/LSelect";
import { useEffect, useState, useCallback } from "react";
import { PageHeader } from "@/components/common/PageHeader";
import { loyaltyService } from "@/services";
import { useAuth } from "@/context/AuthContext";
import { apiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { Split, Loader2, Send, Save, Link2 } from "lucide-react";

const LABELS = {
  customer360: "Customer 360", crm: "CRM", campaigns: "Campaigns", whatsapp: "WhatsApp",
  email: "Email", sms: "SMS", mobile: "Mobile App", website: "Website",
};

export default function OmnichannelPage() {
  const { hasPermission } = useAuth();
  const canManage = hasPermission("LOYALTY.UPDATE");
  const [channels, setChannels] = useState({});
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [links, setLinks] = useState([]);
  // notify tester
  const [notify, setNotify] = useState({ customerId: "", channel: "mobile", message: "You earned 500 points!" });
  const [sending, setSending] = useState(false);
  // campaign link
  const [link, setLink] = useState({ campaignId: "", campaignName: "", programId: "" });
  const [programs, setPrograms] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [c, l, p] = await Promise.all([loyaltyService.omniConfig(), loyaltyService.campaignLinks(), loyaltyService.programs()]);
      setChannels(c.channels); setAvailable(c.available); setLinks(l.items || []); setPrograms(p.items || []);
    } catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveConfig = async () => {
    setSaving(true);
    try { await loyaltyService.setOmniConfig(channels); toast.success("Channels updated"); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const sendNotify = async () => {
    if (!notify.customerId.trim()) { toast.error("Customer ID required"); return; }
    setSending(true);
    try { const r = await loyaltyService.omniNotify(notify); toast.success(`Notification ${r.status}`); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const addLink = async () => {
    if (!link.campaignId.trim() || !link.programId) { toast.error("Campaign ID and program required"); return; }
    try { await loyaltyService.createCampaignLink(link); toast.success("Campaign linked"); setLink({ campaignId: "", campaignName: "", programId: "" }); load(); }
    catch (e) { toast.error(apiErrorMessage(e.response?.data?.detail)); }
  };

  if (loading) return <div className="flex justify-center py-20 text-muted-foreground"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  return (
    <div data-testid="omnichannel-page">
      <PageHeader title="Omnichannel Loyalty" subtitle="Connect loyalty across Customer 360, CRM, Campaigns & messaging channels" />

      <div className="rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-700 dark:text-amber-400 text-xs px-4 py-2 mb-5" data-testid="omni-mock-note">
        Message delivery (WhatsApp / Email / SMS) is mocked in this environment — events are recorded, not sent. Connect providers at deployment.
      </div>

      {/* Channels */}
      <div className="bg-card border border-border rounded-2xl shadow-sm p-5 mb-6" data-testid="omni-channels-card">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-semibold text-foreground flex items-center gap-2"><Split className="h-4.5 w-4.5 text-primary" /> Channels</h3>
          {canManage && <button onClick={saveConfig} disabled={saving} className="inline-flex items-center gap-2 bg-primary text-primary-foreground rounded-lg px-3 py-1.5 text-sm font-semibold disabled:opacity-50" data-testid="omni-save-button">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save</button>}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {available.map((c) => (
            <label key={c} className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 cursor-pointer hover:bg-muted/40" data-testid={`omni-channel-${c}`}>
              <input type="checkbox" checked={!!channels[c]} disabled={!canManage} onChange={(e) => setChannels({ ...channels, [c]: e.target.checked })} className="accent-primary" />
              <span className="text-sm text-foreground">{LABELS[c] || c}</span>
            </label>
          ))}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Campaign link */}
        <div className="bg-card border border-border rounded-2xl shadow-sm p-5" data-testid="omni-campaign-card">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2"><Link2 className="h-4.5 w-4.5 text-primary" /> Campaign Integration</h3>
          {canManage && (
            <div className="space-y-3 mb-4">
              <input value={link.campaignId} onChange={(e) => setLink({ ...link, campaignId: e.target.value })} placeholder="Campaign ID" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="link-campaign-id" />
              <input value={link.campaignName} onChange={(e) => setLink({ ...link, campaignName: e.target.value })} placeholder="Campaign name (optional)" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" />
              <LSelect value={link.programId} onChange={(e) => setLink({ ...link, programId: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="link-program-select">
                <option value="">Select program…</option>
                {programs.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
              </LSelect>
              <button onClick={addLink} className="w-full bg-primary/10 text-primary rounded-lg px-3 py-2 text-sm font-semibold hover:bg-primary/20" data-testid="link-add-button">Link Campaign</button>
            </div>
          )}
          <div className="space-y-2">
            {links.length === 0 ? <p className="text-sm text-muted-foreground py-4 text-center">No campaign links yet.</p> : links.map((l) => (
              <div key={l.id} className="flex justify-between text-sm border-b border-border pb-2 last:border-0">
                <span className="text-foreground">{l.campaignName || l.campaignId}</span>
                <span className="text-muted-foreground">→ {l.programName}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Notify tester */}
        <div className="bg-card border border-border rounded-2xl shadow-sm p-5" data-testid="omni-notify-card">
          <h3 className="font-semibold text-foreground mb-4 flex items-center gap-2"><Send className="h-4.5 w-4.5 text-primary" /> Send Notification (mock)</h3>
          <div className="space-y-3">
            <input value={notify.customerId} onChange={(e) => setNotify({ ...notify, customerId: e.target.value })} placeholder="Customer ID" className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="notify-customer-input" />
            <LSelect value={notify.channel} onChange={(e) => setNotify({ ...notify, channel: e.target.value })} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="notify-channel-select">
              {available.filter((c) => channels[c]).map((c) => <option key={c} value={c}>{LABELS[c] || c}</option>)}
            </LSelect>
            <textarea value={notify.message} onChange={(e) => setNotify({ ...notify, message: e.target.value })} rows={2} className="w-full rounded-lg border border-border bg-background px-3 py-2 text-sm" data-testid="notify-message-input" />
            <button onClick={sendNotify} disabled={sending || !canManage} className="w-full inline-flex items-center justify-center gap-2 bg-primary text-primary-foreground rounded-lg px-3 py-2 text-sm font-semibold disabled:opacity-50" data-testid="notify-send-button">
              {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Queue Notification
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
